import { FC, useEffect, useState, useCallback } from 'react';
import EditorLink from 'lowcode/components/EditorLink';
import { message, Tooltip } from 'antd-v5';
import throttle from 'lodash/throttle';
import { shopnowMay, shopSeller, appUrl } from '../../constants';
import { TaskIcon4, TaskIcon5, QuestionIcon } from '../../Svg';
import { useLang } from '@/app/utils/useLang';
import { useMembersStore } from '@/components/MembersDay/MembersProvider';
import { getMemberHolidayTask, makeMemberHolidayRewardEarn, makeMemberHolidayRewardEarn2 } from '@/components/MembersDay/apis';
import './index.scss';
import getLocal from '@/app/utils/getLocal';
import { signupCookie } from '@/utils/cookies/signupCookie';

interface IProps {
    handleShowEcoCredits: (type: string) => void;
}
type ISource = 'Y25_KNOW_BRAND' | 'Y25_KNOW_MEMBER' | 'Y25_SHARE_STORY' | 'Y25_KNOW_PRODUCT' | 'Y25_KNOW_APP' | 'Y25_SHARE_ACTIVITY';

const MAX_REFERRAL_NUM = 20;
const MAX_PURCHASE_NUM = 3;

const PointsTask: FC<IProps> = ({ handleShowEcoCredits }) => {
    const { t } = useLang('membersday.membersdaytask25');
    const { campaignInfo, isLogin, inBlackList } = useMembersStore();
    const { locale, pathname } = getLocal();
    // const shopnowUrl = shopnowMay[locale] || `https://${locale}.ecoflow.com/pages/members-festival`;
    const shopSellerUrl = shopSeller[locale] || `https://${locale}.ecoflow.com`;
    const redirectAppUrl = appUrl[locale] || `https://www.ecoflow.com/us/oasis-home-energy-management-system`;

    const handleInBlackList = () => {
        if (inBlackList) {
            message.error(t('membersdaybanner25_no_eligible'));
            return;
        }
    };

    const copyText = (text: string) => {
        // 优先使用 Clipboard API
        if (navigator?.clipboard && window?.isSecureContext) {
            navigator.clipboard
                .writeText(text)
                .then(() => {
                    message.success(t('membersdaybanner25_copy_success'));
                })
                .catch(() => {
                    fallbackCopyText(text);
                });
        } else {
            fallbackCopyText(text);
        }
    };

    const fallbackCopyText = (text: string) => {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
            const successful = document.execCommand('copy');
            if (successful) {
                message.success(t('membersdaybanner25_copy_success'));
            } else {
                message.error(t('membersdaybanner25_copy_fail'));
            }
        } catch (err) {
            message.error(t('membersdaybanner25_copy_fail'));
        }
        document.body.removeChild(textArea);
    };

    const toRegisterPage = (title: any) => {
        if (!isLogin && typeof window !== 'undefined') {
            signupCookie?.({
                sector_name: title,
                entry_position: 4,
            });
            window.location.href = `/${locale}/register?redirect=${encodeURIComponent(`/${locale}${pathname}#energy`)}`;
        }
    };

    const toShopify = () => {
        handleInBlackList();
        window.open(shopSellerUrl, '_blank');
    };

    const isExceedTime = (endTime: string) => {
        const time = new Date().getTime();
        const end = new Date(endTime).getTime();
        return time > end;
    };

    const genDataLayer = (index: number, item: any) => {
        const dataLayerMap: Record<number, any> = {
            0: {
                event: 'uaEvent',
                event_name: 'activity_join',
                button_name: `${t('task1_title')}_${item.defaultBtnText}`,
            },
            3: {
                event: 'uaEvent',
                event_name: 'buy',
                product_name: 'none',
                position: 'Members-Festival_Task 4_Learn more about EcoFlow Best Sellers',
            },
            6: {
                event: 'uaEvent',
                event_name: 'jump_button',
                position: 'Members-Festival_Task 7_Learn more about the EcoFlow app',
                button_name: `${t('task9_title')}_${t('task_btn_completetask')}`,
                url: redirectAppUrl,
            },
            8: {
                event: 'uaEvent',
                event_name: 'referral_button',
                position: 'Members-Festival_Task 9_Share this page with friends',
                button_name: 'Share this page with friends_Complete Task',
            },
        };

        return (
            dataLayerMap[index] || {
                event: 'uaEvent',
                event_name: 'activity_button',
                page_name: 'members-festival',
                list_name: item.title,
                button_name: item.defaultBtnText,
                function: 'Anchor',
            }
        );
    };

    const initData = () => {
        if (!campaignInfo?.campaignId || !isLogin) return;
        /**
         * firstConsumeFlag 是否已首次下单 true:是 false:否
         * knowBrandFlag 是否已了解品牌 true:是 false:否
         * knowMemberFlag 是否已了解会员 true:是 false:否
        if (!campaignInfo?.campaignId || !isLogin) return;
        /**
         * firstConsumeFlag 是否已首次下单 true:是 false:否
         * knowBrandFlag 是否已了解品牌 true:是 false:否
         * knowMemberFlag 是否已了解会员 true:是 false:否
         * referralCount 推荐人数
         * repeatConsumeCount 复购数量
         * shareStoryFlag 是否已分享故事 true:是 false:否
         * knowProductFlag	是否已了解热卖品 true:是 false:否
         *  */
        getMemberHolidayTask(locale, campaignInfo.campaignId).then((res) => {
            if (res.code !== '0' || !res.data) return message.error(res.message);

            const newList = [...list]; // list固定有9个
            const {
                knowBrandFlag,
                knowMemberFlag,
                referralCount,
                shareStoryFlag,
                firstConsumeFlag,
                repeatConsumeCount,
                knowProductFlag,
                knowAppFlag,
                shareActivityFlag,
            } = res.data;

            // 已登陆的话则是完成
            if (isLogin) newList[0].isFinsh = true;
            if (knowBrandFlag) newList[1].isFinsh = true;
            if (knowMemberFlag) newList[2].isFinsh = true;
            if (knowProductFlag) newList[3].isFinsh = true;

            newList[3].title = t.rich('task8_title', { referralCount: referralCount > MAX_REFERRAL_NUM ? MAX_REFERRAL_NUM : referralCount });
            if (referralCount >= MAX_REFERRAL_NUM) newList[3].isFinsh = true;

            if (firstConsumeFlag) {
                newList[4].title = newList[4].title.replace('0', '1');
                newList[4].isFinsh = true;
            }

            newList[5].title = t.rich('task7_title', { repeatConsumeCount: repeatConsumeCount > MAX_PURCHASE_NUM ? MAX_PURCHASE_NUM : repeatConsumeCount });
            if (repeatConsumeCount >= MAX_PURCHASE_NUM) newList[5].isFinsh = true;

            if (knowAppFlag) newList[6].isFinsh = true;
            if (shareStoryFlag) newList[7].isFinsh = true;
            if (shareActivityFlag) newList[8].isFinsh = true;

            // campaignInfo变动后函数需要更新
            newList[0].onClick = () => toRegisterPage(newList[0].title);
            newList[1].onClick = () => handleFinsh('Y25_KNOW_BRAND');
            newList[2].onClick = () => handleFinsh('Y25_KNOW_MEMBER');
            newList[3].onClick = () => handleFinsh('Y25_KNOW_PRODUCT');
            newList[4].onClick = toShopify;
            newList[5].onClick = toShopify;
            newList[6].onClick = () => handleFinsh('Y25_KNOW_APP');
            newList[7].onClick = () => handleFinsh('Y25_SHARE_STORY');
            newList[8].onClick = () => handleFinsh('Y25_SHARE_ACTIVITY');

            setList(newList);
        });
    };

    const handleFinsh = throttle(
        (source: ISource) => {
            handleInBlackList();

            // 当前时间是否已过期，过去点击无反应
            const isOutTime = isExceedTime((campaignInfo as any)?.campaignEndTime || '');
            if (isOutTime) return;

            if (campaignInfo) {
                if (!['Y25_KNOW_PRODUCT', 'Y25_KNOW_APP', 'Y25_SHARE_ACTIVITY', 'Y25_SHARE_STORY'].includes(source)) {
                    makeMemberHolidayRewardEarn(locale, {
                        campaignId: campaignInfo.campaignId,
                        source: source as 'Y25_KNOW_BRAND' | 'Y25_KNOW_MEMBER',
                    }).then((res) => {
                        if (res?.code !== '0') return message.error(res.message);
                        if (source === 'Y25_KNOW_BRAND') {
                            document.getElementById('introduction')?.scrollIntoView(true);
                            handleShowEcoCredits('a');
                        }
                        if (source === 'Y25_KNOW_MEMBER') {
                            document.getElementById('introduction')?.scrollIntoView(true);
                            handleShowEcoCredits('b');
                        }
                        initData();
                    });
                } else {
                    makeMemberHolidayRewardEarn2(locale, {
                        campaignId: campaignInfo.campaignId,
                        scene: source,
                    }).then((res) => {
                        if (res?.code !== '0') return message.error(res.message);
                        if (source === 'Y25_SHARE_ACTIVITY') {
                            copyText(`${window.location.origin}/${locale}/members-festival`);
                        }
                        if (source === 'Y25_KNOW_PRODUCT') {
                            window.open(shopSellerUrl, '_blank');
                        }
                        if (source === 'Y25_KNOW_APP') {
                            window.open(redirectAppUrl, '_blank');
                        }
                        if (source === 'Y25_SHARE_STORY') {
                            document.getElementById('UGC')?.scrollIntoView(true);
                        }
                        initData();
                    });
                }
            }
        },
        1000,
        { trailing: false },
    );

    const defaultList = [
        {
            title: t('task1_title'),
            tips: '',
            awardList: [
                {
                    icon: <TaskIcon4 />,
                    text: t('task1_ecocredits'),
                },
                {
                    icon: <TaskIcon5 />,
                    text: t('task1_points'),
                },
            ],
            defaultBtnText: t('task_btn_completetask'),
            isFinsh: false,
            onClick: toRegisterPage(t('task1_title')),
        },
        {
            title: t('task2_title'),
            tips: '',
            awardList: [
                {
                    icon: <TaskIcon4 />,
                    text: t('task2_ecocredits'),
                },
                {
                    icon: <TaskIcon5 />,
                    text: t('task2_points'),
                },
            ],
            defaultBtnText: t('task_btn_completetask'),
            isFinsh: false,
            onClick: () => handleFinsh('Y25_KNOW_BRAND'),
        },
        {
            title: t('task3_title'),
            tips: '',
            awardList: [
                {
                    icon: <TaskIcon4 />,
                    text: t('task3_ecocredits'),
                },
                {
                    icon: <TaskIcon5 />,
                    text: t('task3_points'),
                },
            ],
            defaultBtnText: t('task_btn_completetask'),
            isFinsh: false,
            onClick: () => handleFinsh('Y25_KNOW_MEMBER'),
        },
        {
            title: t('task8_title'),
            tips: '',
            awardList: [
                {
                    icon: <TaskIcon5 />,
                    text: t('task8_points'),
                },
                {
                    icon: <></>,
                    text: '',
                },
            ],
            defaultBtnText: t('task_btn_purchasenow'),
            isFinsh: false,
            onClick: () => handleFinsh('Y25_KNOW_PRODUCT'),
        },
        {
            title: t('task6_title'),
            tips: t('task6_title_tips'),
            awardList: [
                {
                    icon: <TaskIcon4 />,
                    text: t('task6_ecocredits'),
                },
                {
                    icon: <TaskIcon5 />,
                    text: t('task6_points'),
                },
            ],
            defaultBtnText: t('task_btn_purchasenow'),
            isFinsh: false,
            onClick: toShopify,
        },
        {
            title: t.rich('task7_title', {
                repeatConsumeCount: 0,
            }),
            tips: '',
            awardList: [
                {
                    icon: <TaskIcon4 />,
                    text: t('task7_ecocredits'),
                },
                {
                    icon: <TaskIcon5 />,
                    text: t('task7_points'),
                },
            ],
            defaultBtnText: t('task_btn_purchasenow'),
            isFinsh: false, // 复购数量
            onClick: toShopify,
        },
        {
            title: t('task9_title'),
            tips: '',
            awardList: [
                {
                    icon: <TaskIcon5 />,
                    text: t('task9_points'),
                },
                {
                    icon: <></>,
                    text: '',
                },
            ],
            defaultBtnText: t('task_btn_completetask'),
            isFinsh: false,
            onClick: () => handleFinsh('Y25_KNOW_APP'),
        },
        {
            title: t('task5_title'),
            tips: '',
            awardList: [
                {
                    icon: <TaskIcon5 />,
                    text: t('task5_points'),
                },
                {
                    icon: <></>,
                    text: '',
                },
            ],
            defaultBtnText: t('task_btn_completetask'),
            isFinsh: false,
            onClick: () => handleFinsh('Y25_SHARE_STORY'),
        },
        {
            title: t('task10_title'),
            tips: '',
            awardList: [
                {
                    icon: <TaskIcon5 />,
                    text: t('task10_points'),
                },
                {
                    icon: <></>,
                    text: '',
                },
            ],
            defaultBtnText: t('task_btn_completetask'),
            isFinsh: false,
            onClick: () => handleFinsh('Y25_SHARE_ACTIVITY'),
        },
    ];

    const [list, setList] = useState(defaultList);

    useEffect(() => {
        if (campaignInfo?.campaignId || isLogin) {
            initData();
        }
    }, [campaignInfo, isLogin]);

    return (
        <div className={'PointsTask'}>
            <div className="task-list">
                {list.map((item, index) => (
                    <div className={`task-item task-item-${index + 1}`} key={index}>
                        <span className="index">{index + 1}</span>
                        <p className="task-title">
                            {item.title}
                            {item.tips && (
                                <Tooltip arrow={false} placement="top" title={<div style={{ whiteSpace: 'pre-line' }}>{item.tips}</div>}>
                                    <QuestionIcon />
                                </Tooltip>
                            )}
                        </p>
                        <div className="task-content">
                            <ul className="award-list">
                                {item.awardList.map((awardItem: any, index: number) => (
                                    <li className={`award-item award-item-${index + 1}`} key={awardItem.text}>
                                        {awardItem.icon}
                                        <span className="text">
                                            {awardItem.text}
                                            {awardItem.tips && (
                                                <Tooltip arrow={false} placement="top" title={<div style={{ whiteSpace: 'pre-line' }}>{awardItem.tips}</div>}>
                                                    <QuestionIcon />
                                                </Tooltip>
                                            )}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                            {isLogin ? (
                                // <EditorLink
                                //     className={`complete-btn ${item.isFinsh && !inBlackList ? 'disabled' : ''}`}
                                //     dataLayer={
                                //         index < 5
                                //             ? {
                                //                   event: 'uaEvent',
                                //                   event_name: 'activity_button',
                                //                   page_name: 'members-festival',
                                //                   list_name: item.title,
                                //                   button_name: item.defaultBtnText,
                                //                   function: 'Anchor',
                                //               }
                                //             : { event: 'uaEvent', event_name: 'buy', product_name: 'none', position: `Members-Festival_${item.title}` }
                                //     }
                                //     onClick={item.isFinsh && !inBlackList ? () => null : item.onClick}
                                // >
                                //     {item.isFinsh && !inBlackList ? t('task_btn_finished') : item.defaultBtnText}
                                // </EditorLink>
                                <EditorLink
                                    className={`complete-btn ${item.isFinsh && !inBlackList ? 'disabled' : ''}`}
                                    dataLayer={genDataLayer(index, item)}
                                    onClick={item.isFinsh && !inBlackList ? () => null : item.onClick}
                                >
                                    {item.isFinsh && !inBlackList ? t('task_btn_finished') : item.defaultBtnText}
                                </EditorLink>
                            ) : (
                                <EditorLink
                                    dataLayer={{
                                        event: 'uaEvent',
                                        event_name: 'activity_join',
                                        button_name: `${item.title}_${t('task_btn_joinnow')}`,
                                        // list_name: item.title,
                                    }}
                                    className={`complete-btn`}
                                    onClick={() => toRegisterPage(item.title)}
                                >
                                    {t('task_btn_joinnow')}
                                </EditorLink>
                            )}

                            {/* 第一条特殊处理 添加一段文案 */}
                            {/* {index === 0 && !isLogin && (
                                <p className="first-task-login">
                                    {t.rich('membersdayalready', {
                                        link: (text: string) => (
                                            <EditorLink
                                                dataLayer={{
                                                    event: 'uaEvent',
                                                    event_name: 'activity_join',
                                                    button_name: `${t('task1_title')}_${text}`,
                                                    list_name: t('task1_title'),
                                                }}
                                                href={`/${locale}/login?redirect=${encodeURIComponent(`/${locale}${pathname}#energy`)}`}
                                            >
                                                {text}
                                            </EditorLink>
                                        ),
                                    })}
                                </p>
                            )} */}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default PointsTask;
